import { create } from 'zustand';
import type { User } from '../types/api';
import api from '../lib/api';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string) => Promise<void>;
  logout: () => void;
  fetchMe: () => Promise<void>;
  /**
   * Setzt den angemeldeten Benutzer, ohne ihn neu zu laden.
   *
   * Gedacht fuer Faelle, in denen der Server den aktualisierten Benutzer
   * ohnehin zurueckgibt - etwa beim Speichern des eigenen Profils. `fetchMe`
   * waere dort falsch: Es wirft bei *jedem* Fehlschlag den Token weg, ein
   * kurzer Netzaussetzer nach erfolgreichem Speichern haette also abgemeldet.
   */
  setUser: (user: User) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: true,
  login: async (token: string) => {
    localStorage.setItem('token', token);
    set({ isAuthenticated: true, isLoading: true });
    try {
      const response = await api.get<User>('/auth/me');
      set({ user: response.data, isLoading: false });
    } catch (error) {
      localStorage.removeItem('token');
      set({ isAuthenticated: false, user: null, isLoading: false });
      throw error;
    }
  },
  setUser: (user: User) => set({ user, isAuthenticated: true, isLoading: false }),
  logout: () => {
    localStorage.removeItem('token');
    set({ user: null, isAuthenticated: false, isLoading: false });
  },
  fetchMe: async () => {
    if (!localStorage.getItem('token')) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }
    try {
      const response = await api.get<User>('/auth/me');
      set({ user: response.data, isAuthenticated: true, isLoading: false });
    } catch {
      localStorage.removeItem('token');
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },
}));
